/**
 * Popup crítico para veículos com bateria ≤ 5%.
 *
 * Dados que reúne on-demand:
 *   - Endereço atual (proxy Nominatim: /api/geocode/reverse)
 *   - Motorista + linha cadastrada (opcional, via /api/relatorios/turnos)
 *   - Linha em operação real (via /api/olhovivo/linha-do-veiculo — apenas para
 *     identificar em qual linha o veículo está, política do usuário)
 *   - ETA ao ponto final: **PENDENTE** — depende do cadastro de linhas com
 *     lat/lon que o usuário vai fornecer (Fase B). Enquanto isso mostra
 *     "aguardando cadastro".
 *
 * Silenciar por 5 min via `sessionStorage` para evitar loop de re-abertura
 * quando o polling da tela detectar o mesmo veículo em SOC ≤ 5%.
 */
import { useEffect, useMemo, useState } from "react";

export interface CriticalVehicleInput {
  vin: string;
  prefix: string | null;
  socPct: number | null;
  rangeKm: number | null;
  lat: number | null;
  lon: number | null;
  speedKmh?: number | null;
}

interface GtfsTerminal {
  routeId: string;
  direction: number | null;
  headsign: string | null;
  stopId: string;
  stopName: string;
  lat: number | null;
  lon: number | null;
}

/**
 * Distância haversine (km) entre dois pontos.
 * Precisão suficiente para ETA operacional (<50km, erro < 0.5%).
 */
function haversineKm(
  aLat: number,
  aLon: number,
  bLat: number,
  bLon: number,
): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLon = toRad(bLon - aLon);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

interface GeocodeResponse {
  formatted: string;
  road: string | null;
  houseNumber: string | null;
}

interface OlhoVivoLinha {
  linha: string;
  sentido: number;
  letreiro: string;
  origem: string;
  destino: string;
  ultimoAtualizado: string;
}

interface ShiftLite {
  driverName: string | null;
  lineCode: string | null;
  lineName: string | null;
}

interface Props {
  vehicle: CriticalVehicleInput;
  onClose: () => void;
  onNavigateToMap: (vin: string) => void;
}

export function CriticalBatteryAlert({ vehicle, onClose, onNavigateToMap }: Props) {
  const [address, setAddress] = useState<string | null>(null);
  const [olhovivo, setOlhovivo] = useState<OlhoVivoLinha | null>(null);
  const [olhovivoError, setOlhovivoError] = useState<string | null>(null);
  const [shift, setShift] = useState<ShiftLite | null>(null);
  const [terminals, setTerminals] = useState<GtfsTerminal[] | null>(null);
  const [avgSpeedKmh, setAvgSpeedKmh] = useState<number | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Fetch endereço
  useEffect(() => {
    if (vehicle.lat === null || vehicle.lon === null) return;
    let alive = true;
    fetch(
      `/api/geocode/reverse?lat=${vehicle.lat}&lon=${vehicle.lon}`,
      { credentials: "include" },
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((body: GeocodeResponse | null) => {
        if (alive && body) setAddress(body.formatted);
      })
      .catch(() => {
        /* silencia — coordenada continua disponível */
      });
    return () => {
      alive = false;
    };
  }, [vehicle.lat, vehicle.lon]);

  // Fetch turno ativo (motorista + linha cadastrada)
  useEffect(() => {
    let alive = true;
    fetch(
      `/api/relatorios/turnos?vehicleVin=${encodeURIComponent(vehicle.vin)}&limit=1`,
      { credentials: "include" },
    )
      .then(async (r) => (r.ok ? r.json() : null))
      .then(
        (body: {
          items?: Array<{
            driverName: string | null;
            lineCode: string | null;
            lineName: string | null;
            endedAt: string | null;
          }>;
        } | null) => {
          if (!alive || !body?.items?.length) return;
          const item = body.items[0]!;
          // Só usa se ainda está aberto (endedAt null)
          if (item.endedAt === null) {
            setShift({
              driverName: item.driverName,
              lineCode: item.lineCode,
              lineName: item.lineName,
            });
          }
        },
      )
      .catch(() => {
        /* silencia */
      });
    return () => {
      alive = false;
    };
  }, [vehicle.vin]);

  // Fetch linha operada AGORA (Olho Vivo)
  useEffect(() => {
    if (!vehicle.prefix) return;
    let alive = true;
    fetch(
      `/api/olhovivo/linha-do-veiculo?prefix=${encodeURIComponent(vehicle.prefix)}`,
      { credentials: "include" },
    )
      .then(async (r) => {
        if (r.status === 503) {
          throw new Error("token_missing");
        }
        if (r.status === 404) {
          throw new Error("not_in_snapshot");
        }
        if (!r.ok) throw new Error(`http_${r.status}`);
        return r.json();
      })
      .then((body: OlhoVivoLinha) => {
        if (alive) setOlhovivo(body);
      })
      .catch((err) => {
        if (alive) setOlhovivoError((err as Error).message);
      });
    return () => {
      alive = false;
    };
  }, [vehicle.prefix]);

  const linhaDisplay = useMemo(() => {
    if (olhovivo) return `${olhovivo.letreiro} · ${olhovivo.origem} → ${olhovivo.destino}`;
    if (shift?.lineCode) return `${shift.lineCode} — ${shift.lineName ?? "sem nome"}`;
    if (olhovivoError === "token_missing") return "Olho Vivo desativado (sem token)";
    if (olhovivoError === "not_in_snapshot")
      return "Veículo não localizado no snapshot Olho Vivo";
    return "…";
  }, [olhovivo, shift, olhovivoError]);

  // Depois que Olho Vivo confirma o letreiro, busca ponto final no GTFS.
  useEffect(() => {
    if (!olhovivo?.letreiro) return;
    let alive = true;
    fetch(
      `/api/gtfs/linha/${encodeURIComponent(olhovivo.letreiro)}/ponto-final`,
      { credentials: "include" },
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((body: { terminals?: GtfsTerminal[] } | null) => {
        if (alive && body?.terminals) setTerminals(body.terminals);
      })
      .catch(() => {
        /* silencia — ETA fica indisponível */
      });
    return () => {
      alive = false;
    };
  }, [olhovivo?.letreiro]);

  // Velocidade média das últimas 3h de rastro (útil pra ETA quando o veículo
  // está parado no momento). Cai pra range_km / duração se não houver rastro.
  useEffect(() => {
    let alive = true;
    fetch(
      `/api/telemetria-frota/veiculo/${encodeURIComponent(vehicle.vin)}/detail?minutes=180`,
      { credentials: "include" },
    )
      .then((r) => (r.ok ? r.json() : null))
      .then(
        (body: {
          trail?: Array<{ ts: string; lat: number; lon: number }>;
        } | null) => {
          if (!alive || !body?.trail || body.trail.length < 2) return;
          const t = body.trail;
          let km = 0;
          for (let i = 1; i < t.length; i++) {
            const a = t[i - 1]!;
            const b = t[i]!;
            km += haversineKm(a.lat, a.lon, b.lat, b.lon);
          }
          const startMs = new Date(t[0]!.ts).getTime();
          const endMs = new Date(t[t.length - 1]!.ts).getTime();
          const hours = (endMs - startMs) / 3600_000;
          if (hours > 0.05) {
            setAvgSpeedKmh(km / hours);
          }
        },
      )
      .catch(() => {
        /* silencia */
      });
    return () => {
      alive = false;
    };
  }, [vehicle.vin]);

  // Calcula ETA ao ponto final da direção mais próxima.
  const etaBlock = useMemo(() => {
    if (
      !terminals ||
      terminals.length === 0 ||
      vehicle.lat === null ||
      vehicle.lon === null
    ) {
      return null;
    }
    // Pega o terminal MAIS PRÓXIMO (heurística: no fluxo real, direction do
    // Olho Vivo poderia narrow mais, mas o headsign nem sempre bate).
    let best: { terminal: GtfsTerminal; distKm: number } | null = null;
    for (const term of terminals) {
      if (term.lat === null || term.lon === null) continue;
      const distKm = haversineKm(vehicle.lat, vehicle.lon, term.lat, term.lon);
      if (!best || distKm < best.distKm) best = { terminal: term, distKm };
    }
    if (!best) return null;
    // Fator 1.4: vias reais são maiores que a linha reta (empírico São Paulo).
    const roadKm = best.distKm * 1.4;
    // Se autonomia < distância, sinaliza "não alcança".
    const rangeKm = vehicle.rangeKm ?? Infinity;
    const alcanca = rangeKm >= roadKm;
    // Velocidade: prefere avg das últimas 3h; senão speedKmh atual; senão 15 km/h
    // (velocidade média típica de ônibus urbano SP em horário comum).
    const v =
      avgSpeedKmh !== null && avgSpeedKmh > 3
        ? avgSpeedKmh
        : vehicle.speedKmh && vehicle.speedKmh > 3
          ? vehicle.speedKmh
          : 15;
    const etaMin = Math.round((roadKm / v) * 60);
    return {
      terminal: best.terminal,
      distKm: roadKm,
      etaMin,
      alcanca,
      velocidadeUsada: v,
    };
  }, [terminals, vehicle.lat, vehicle.lon, vehicle.rangeKm, vehicle.speedKmh, avgSpeedKmh]);

  const socColor =
    vehicle.socPct !== null && vehicle.socPct <= 5
      ? "var(--nx-danger)"
      : "var(--nx-warn, var(--nx-warning))";

  return (
    <div
      className="nx-modal"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="nx-crit-battery-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="nx-modal__card"
        style={{
          maxWidth: 520,
          width: "min(94vw, 520px)",
          borderTop: `4px solid ${socColor}`,
        }}
      >
        <div className="nx-modal__head">
          <h2
            id="nx-crit-battery-title"
            style={{ margin: 0, color: socColor, display: "flex", gap: 8, alignItems: "center" }}
          >
            <span aria-hidden="true">⚠️</span>
            Bateria crítica: {vehicle.socPct?.toFixed(0)}%
          </h2>
          <button
            className="nx-modal__close"
            onClick={onClose}
            aria-label="Fechar"
            type="button"
          >
            ×
          </button>
        </div>

        <div className="nx-modal__body" style={{ display: "grid", gap: 12 }}>
          <div style={{ fontSize: ".95rem", lineHeight: 1.5 }}>
            O ônibus <b>{vehicle.prefix ?? vehicle.vin}</b> está com{" "}
            <b style={{ color: socColor }}>{vehicle.socPct?.toFixed(1)}%</b> de bateria
            e cerca de <b>{vehicle.rangeKm ?? "—"} km</b> de autonomia. Considere despachar
            um reforço ou orientar o motorista a buscar recarga.
          </div>

          <Row label="Localização" value={address ?? "carregando endereço…"} />
          <Row
            label="Coordenadas"
            value={
              vehicle.lat !== null && vehicle.lon !== null
                ? `${vehicle.lat.toFixed(5)}, ${vehicle.lon.toFixed(5)}`
                : "—"
            }
            mono
          />
          <Row label="Motorista" value={shift?.driverName ?? "—"} />
          <Row label="Linha em operação" value={linhaDisplay} />
          {etaBlock ? (
            <>
              <Row
                label="Ponto final"
                value={`${etaBlock.terminal.stopName}${etaBlock.terminal.headsign ? ` (${etaBlock.terminal.headsign})` : ""}`}
              />
              <Row
                label="Distância estimada"
                value={`${etaBlock.distKm.toFixed(1)} km`}
              />
              <Row
                label="ETA ao ponto final"
                value={
                  etaBlock.alcanca
                    ? `~${etaBlock.etaMin} min (a ${etaBlock.velocidadeUsada.toFixed(0)} km/h)`
                    : `⚠️ AUTONOMIA INSUFICIENTE (${etaBlock.distKm.toFixed(1)} km > ${vehicle.rangeKm ?? "?"} km)`
                }
              />
            </>
          ) : (
            <Row
              label="ETA ao ponto final"
              value={
                terminals === null
                  ? "Consultando GTFS…"
                  : terminals.length === 0
                    ? "Linha não encontrada no GTFS"
                    : "Sem coordenadas para calcular"
              }
              muted
            />
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 8,
              marginTop: 8,
            }}
          >
            <button
              type="button"
              className="nx-btn nx-btn--secondary"
              onClick={() => {
                try {
                  sessionStorage.setItem(
                    `nx-crit-snooze-${vehicle.vin}`,
                    String(Date.now() + 5 * 60_000),
                  );
                } catch {
                  /* private mode */
                }
                onClose();
              }}
            >
              Silenciar por 5 min
            </button>
            <button
              type="button"
              className="nx-btn"
              onClick={() => onNavigateToMap(vehicle.vin)}
            >
              Ver no mapa
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  mono,
  muted,
}: {
  label: string;
  value: string;
  mono?: boolean;
  muted?: boolean;
}) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "150px 1fr", gap: 8 }}>
      <span style={{ color: "var(--nx-mute)", fontSize: ".85rem" }}>{label}</span>
      <span
        className={mono ? "mono" : undefined}
        style={{
          color: muted ? "var(--nx-mute)" : "var(--nx-ink)",
          fontSize: ".9rem",
          fontStyle: muted ? "italic" : "normal",
        }}
      >
        {value}
      </span>
    </div>
  );
}
